import { useState } from 'react';
import { Calculator, Loader2, AlertTriangle, Ban, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { tradeService } from '@/features/trade/services/trade-service';
import type { CustomsSimulationResponse } from '@/features/trade/types/trade.types';
import { GlosarioAduanero } from './glosario-aduanero';

/**
 * Liquidación de la partida — lo que antes era la pantalla "Aduana".
 *
 * **Por qué vive acá.** Había dos pantallas que parecían lo mismo: Aduana
 * (buscar la partida → liquidar) y esta ficha (buscar la partida → ver qué
 * paga). Buscabas dos veces la misma partida para responder una sola pregunta:
 * si estabas liquidando y querías saber si la mercadería estaba prohibida o qué
 * certificado exige, tenías que empezar de nuevo en la otra pantalla.
 *
 * **Por qué NO eran lo mismo, aunque lo parecieran.** Cada simulador tenía un
 * hueco que el otro cubría:
 *
 *  - El de Aduana cobraba el **ICE** y aplicaba el **tope del flete aéreo**
 *    (Art. 27 LGA), pero no mostraba bolivianos, ni los pasos, ni los avisos.
 *  - El de esta ficha mostraba todo eso, pero **liquidaba sin ICE y sin el tope
 *    aéreo**: para un vehículo o una bebida daba de menos, y para un envío
 *    aéreo daba de más.
 *
 * Quedarse con cualquiera de los dos era heredar el hueco del otro. Así que
 * ahora los dos apuntan a `POST /trade/aduana/simular`, que es la unión: el
 * motor real (`customs-duty.service`) con TODO lo que cada uno tenía.
 *
 * Acá no se calcula nada, ni siquiera la conversión a bolivianos: si los números
 * de esta pantalla y los del seguimiento de importaciones difirieran alguna vez,
 * sería un bug, no una diferencia de criterio.
 */

const CLASES_SELECT =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export function SimuladorTributos({
  code,
  omitirAvisos = [],
}: {
  code: string;
  /**
   * Códigos de aviso que esta pantalla ya está diciendo por su cuenta.
   *
   * 🔴 Sin esto la ficha mostraba el mismo aviso de ICE dos veces, a diez
   * centímetros de distancia: uno propio de la pestaña Tributos y otro que
   * devuelve la liquidación. El aviso sigue emitiéndose en el servidor —lo
   * necesitan el seguimiento de importaciones y la publicación de precios—,
   * pero acá se calla el duplicado.
   *
   * Repetir advertencias tiene un costo real: la gente deja de leerlas, y
   * entonces tampoco lee la que sí importa —que la mercadería está prohibida,
   * o que exige un certificado sin el cual no entra—.
   */
  omitirAvisos?: string[];
}) {
  const [fob, setFob] = useState('');
  const [flete, setFlete] = useState('');
  const [seguro, setSeguro] = useState('');
  /** Vacío = el seguro se estima con la tasa. */
  const [tasaSeguro, setTasaSeguro] = useState('1.5');
  const [cantidad, setCantidad] = useState('1');
  const [moneda, setMoneda] = useState<'USD' | 'BOB'>('USD');
  const [via, setVia] = useState<'SEA' | 'AIR' | 'LAND'>('SEA');
  const [tc, setTc] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [r, setR] = useState<CustomsSimulationResponse | null>(null);

  const simular = async () => {
    const nFob = Number(fob);
    if (!Number.isFinite(nFob) || nFob <= 0) {
      setError('Ingresá el valor FOB de la mercadería (mayor a 0).');
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const res = await tradeService.simulateCustoms({
        tariffCode: code,
        fobValue: nFob,
        freightValue: Number(flete) || 0,
        // El seguro se manda solo si lo escribieron. Si va vacío, el servidor lo
        // estima con la tasa — y lo dice en `insuranceWasEstimated`.
        ...(seguro.trim() !== '' ? { insuranceValue: Number(seguro) } : {}),
        ...(Number(tasaSeguro) >= 0 ? { insuranceRate: Number(tasaSeguro) } : {}),
        via,
        quantity: Math.max(1, Math.trunc(Number(cantidad) || 1)),
        currency: moneda,
        ...(Number(tc) > 0 ? { exchangeRate: Number(tc) } : {}),
      });
      setR(res);
      // Si el servidor conoce un TC y el campo está vacío, se ofrece como valor
      // visible y editable — nunca aplicado en silencio.
      if (res.tcSugerido && tc.trim() === '') setTc(String(res.tcSugerido.valor));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : 'No se pudo liquidar.');
      setError(msg);
      setR(null);
    } finally {
      setCargando(false);
    }
  };

  const nf = (x: number) =>
    x.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const res = r?.result;
  const bob = res?.bob;

  /*
    Las filas de la tabla. `bs` es null en las tres primeras a propósito: el
    servidor devuelve los bolivianos solo de CIF para abajo, y multiplicar acá
    para rellenar la columna sería exactamente lo que este componente no hace.
  */
  const filas: Array<{ etiqueta: string; usd?: number; bs?: number; fuerte?: boolean }> = res
    ? [
        { etiqueta: 'FOB', usd: res.fob },
        {
          etiqueta: res.airFreightCapApplied ? 'Flete computable (25 %)' : 'Flete',
          usd: res.freightComputable,
        },
        { etiqueta: 'Seguro', usd: res.insurance },
        { etiqueta: 'CIF (base imponible)', usd: res.cif, bs: bob?.cif, fuerte: true },
        { etiqueta: 'Gravamen arancelario (GA)', usd: res.ga, bs: bob?.ga },
        ...(res.ice > 0 || res.rates.iceRate > 0
          ? [{ etiqueta: 'ICE', usd: res.ice, bs: bob?.ice }]
          : []),
        { etiqueta: 'Base IVA (CIF + GA + ICE)', usd: res.ivaBase },
        { etiqueta: 'IVA importaciones', usd: res.iva, bs: bob?.iva },
        {
          etiqueta: 'Total tributos',
          usd: res.totalTaxes,
          bs: bob?.totalTaxes,
          fuerte: true,
        },
        {
          etiqueta: 'Costo nacionalizado',
          usd: res.nationalizedCost,
          bs: bob?.nationalizedCost,
          fuerte: true,
        },
        ...(res.quantity > 1
          ? [
              {
                etiqueta: `Costo por unidad (${res.quantity} u.)`,
                usd: res.nationalizedCostPerUnit,
                bs: bob?.nationalizedCostPerUnit,
                fuerte: true,
              },
            ]
          : []),
      ]
    : [];

  return (
    <div className="rounded-md border p-4 space-y-4">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Calculator className="h-4 w-4" /> Liquidá tu importación de esta partida
          </p>
          <GlosarioAduanero compacto />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Usa el mismo motor de liquidación que el seguimiento de importaciones — misma fórmula
          legal, mismos avisos. No incluye gastos de internación ni honorarios del despachante.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="sim-fob">Valor FOB *</Label>
          <Input
            id="sim-fob"
            type="number"
            min="0"
            step="any"
            placeholder="0.00"
            value={fob}
            onChange={(e) => setFob(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-flete">Flete hasta frontera</Label>
          <Input
            id="sim-flete"
            type="number"
            min="0"
            step="any"
            placeholder="0.00"
            value={flete}
            onChange={(e) => setFlete(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-seguro">Seguro</Label>
          <Input
            id="sim-seguro"
            type="number"
            min="0"
            step="any"
            placeholder="vacío = se estima"
            value={seguro}
            onChange={(e) => setSeguro(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-tasa-seguro">Tasa del seguro (%)</Label>
          <Input
            id="sim-tasa-seguro"
            type="number"
            min="0"
            max="10"
            step="any"
            value={tasaSeguro}
            onChange={(e) => setTasaSeguro(e.target.value)}
            disabled={seguro.trim() !== ''}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-cantidad">Cantidad de unidades</Label>
          <Input
            id="sim-cantidad"
            type="number"
            min="1"
            step="1"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-via">Vía de transporte</Label>
          <select
            id="sim-via"
            className={CLASES_SELECT}
            value={via}
            onChange={(e) => setVia(e.target.value as typeof via)}
          >
            <option value="SEA">Marítima</option>
            <option value="AIR">Aérea — computa el 25 % del flete</option>
            <option value="LAND">Terrestre</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-moneda">Moneda de los montos</Label>
          <select
            id="sim-moneda"
            className={CLASES_SELECT}
            value={moneda}
            onChange={(e) => setMoneda(e.target.value as 'USD' | 'BOB')}
          >
            <option value="USD">Dólares (USD)</option>
            <option value="BOB">Bolivianos (Bs)</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sim-tc">Tipo de cambio (Bs por USD)</Label>
          <Input
            id="sim-tc"
            type="number"
            min="0"
            step="any"
            placeholder="el último cargado, o a mano"
            value={tc}
            onChange={(e) => setTc(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={simular} disabled={cargando}>
          {cargando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Liquidar
        </Button>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      {r && res && (
        <div className="space-y-3">
          {/* Prohibición primero: si la mercadería no entra al país, los números
              de abajo son solo referencia. */}
          {r.avisos
            .filter((a) => a.nivel === 'PROHIBICION')
            .map((a) => (
              <div
                key={a.codigo}
                className="rounded-md border border-bloqueo-borde bg-bloqueo-suave p-3 text-sm flex gap-2"
              >
                <Ban className="h-4 w-4 shrink-0 mt-0.5 text-bloqueo" />
                <span>
                  {a.mensaje}
                  {a.norma ? ` (${a.norma})` : ''}
                </span>
              </div>
            ))}

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Concepto</TableHead>
                  <TableHead className="text-right">USD</TableHead>
                  <TableHead className="text-right">
                    {res.rates.exchangeRate ? `Bs (TC ${res.rates.exchangeRate})` : 'Bs'}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((f) => (
                  <TableRow key={f.etiqueta}>
                    <TableCell className={`text-sm ${f.fuerte ? 'font-semibold' : ''}`}>
                      {f.etiqueta}
                    </TableCell>
                    <TableCell
                      className={`text-right text-sm tabular-nums ${f.fuerte ? 'font-semibold' : ''}`}
                    >
                      {f.usd !== undefined ? nf(f.usd) : '—'}
                    </TableCell>
                    <TableCell
                      className={`text-right text-sm tabular-nums ${f.fuerte ? 'font-semibold' : ''}`}
                    >
                      {f.bs !== undefined ? (
                        nf(f.bs)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {r.insuranceWasEstimated && (
            <p className="text-xs text-muted-foreground">
              El seguro no se informó: se estimó con el {tasaSeguro} % sobre FOB + flete. Si tenés
              la prima real, cargala arriba.
            </p>
          )}

          {/*
            🔴 Acá decía: "El TC oficial del BCB todavía no se carga solo en
            Prixia: ingresalo a mano arriba". **Era falso.** El sincronizador del
            BCB existe (`bcb-exchange-rate.service.ts`), corre cada 30 minutos por
            BullMQ y escribe una fila por empresa. El texto venía de antes de que
            se implementara y nadie lo actualizó.

            No es un detalle de redacción: le decía al usuario que hiciera a mano
            —y en cada liquidación— un trabajo que el sistema ya hacía. Un aviso
            que describe una limitación que ya no existe es peor que ninguno.
          */}
          {!bob && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                Sin tipo de cambio la liquidación sale solo en USD. Prixia trae el oficial del BCB
                cada media hora — si todavía no hay ninguno, podés traerlo ahora o cargarlo a mano
                arriba.
              </p>
              <BotonTraerTc onListo={(valor) => setTc(String(valor))} />
            </div>
          )}
          {bob && r.tcSugerido && res.rates.exchangeRate === r.tcSugerido.valor && (
            <p className="text-xs text-muted-foreground">
              TC usado: el último cargado en Prixia ({r.tcSugerido.fuente},{' '}
              {/*
                🔴 En UTC, no en la zona del navegador. La fecha se guarda a
                medianoche UTC y en Bolivia (UTC−4) se mostraba **un día antes**:
                un TC cargado el 13 aparecía como del 12. En un dato cuya vigencia
                es por día —y que el Art. 27 LGA exige que sea el de la fecha de
                aceptación de la DUI— el día equivocado invalida la referencia.
              */}
              {new Date(r.tcSugerido.fecha).toLocaleDateString('es-BO', { timeZone: 'UTC' })})
              {r.tcSugerido.desactualizado
                ? ' — tiene más de una semana, verificá el vigente del BCB'
                : ''}
              . Podés pisarlo con el campo de arriba.
            </p>
          )}

          {/*
            El botón aparece solo si el TC que se usó está desactualizado. Con uno
            de hoy no hay nada que traer, y ofrecerlo igual invitaría a apretarlo
            sin motivo.
          */}
          {bob && r.tcSugerido?.desactualizado && (
            <BotonTraerTc onListo={(valor) => setTc(String(valor))} />
          )}

          <div className="rounded-md border p-3 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              De dónde sale cada número (montos en USD)
            </p>
            {res.steps.map((p, i) => (
              <p key={i} className="text-xs font-mono">
                {p}
              </p>
            ))}
          </div>

          {res.legalBasis.length > 0 && (
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Normas aplicadas
              </p>
              {res.legalBasis.map((n) => (
                <p key={n} className="text-xs text-muted-foreground">
                  · {n}
                </p>
              ))}
            </div>
          )}

          {r.avisos
            .filter((a) => a.nivel !== 'PROHIBICION' && !omitirAvisos.includes(a.codigo))
            .map((a) => (
              <div
                key={a.codigo}
                className="rounded-md border border-alerta-borde bg-alerta-suave p-3 text-sm flex gap-2"
              >
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-alerta " />
                <span>
                  {a.mensaje}
                  {a.norma ? ` (${a.norma})` : ''}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * Trae el tipo de cambio oficial del BCB ahora mismo.
 *
 * **Por qué existe si ya hay un sincronizador automático.** El worker corre cada
 * media hora y eso alcanza casi siempre. Pero cuando no —worker caído, sistema
 * recién levantado— el usuario veía "TC del 16-ago" y no tenía nada que apretar:
 * el único botón manual estaba en Datos Maestros → Divisas, otra pantalla y con
 * un permiso que un vendedor no tiene.
 *
 * Al traerlo, además de guardarlo, lo escribe en el campo de arriba: si alguien
 * apretó esto es porque quiere liquidar con ese número, no porque quiera
 * enterarse de que cambió.
 */
function BotonTraerTc({ onListo }: { onListo: (valor: number) => void }) {
  const [trayendo, setTrayendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState<number | null>(null);

  const traer = async () => {
    setTrayendo(true);
    setError(null);
    try {
      const r = await tradeService.actualizarTipoDeCambio();
      if (r.rate == null) {
        setError('El BCB respondió pero sin un valor usable.');
        return;
      }
      setListo(r.rate);
      onListo(r.rate);
    } catch (e: unknown) {
      // El mensaje del servidor explica el caso —el BCB no respondió— mejor que
      // cualquier texto genérico, así que se muestra tal cual si viene.
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'No se pudo traer el tipo de cambio.';
      setError(msg);
    } finally {
      setTrayendo(false);
    }
  };

  if (listo !== null) {
    return (
      <p className="text-xs text-ok">
        Tipo de cambio del BCB traído: <strong>{listo}</strong> Bs por dólar. Ya está en el campo de
        arriba.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <Button type="button" variant="outline" size="sm" onClick={traer} disabled={trayendo}>
        {trayendo ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
        )}
        {trayendo ? 'Consultando al BCB…' : 'Traer el TC oficial del BCB'}
      </Button>
      {error && <p className="text-xs text-alerta">{error}</p>}
    </div>
  );
}
